import React from 'react';
import { Book, Star, Coins, TrendingUp, Users, Award, ArrowRight } from 'lucide-react';

const WelcomePage = ({ userName, onContinue }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Book className="w-16 h-16 text-indigo-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Welcome to Vibely, {userName}! 🎉
          </h1>
          <p className="text-xl text-gray-600">
            Your journey to more book reviews starts here
          </p>
        </div>

        {/* Welcome Bonus */}
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Coins className="w-8 h-8 text-amber-600" />
            <h2 className="text-2xl font-bold text-amber-800">You've Got 50 FREE Coins!</h2>
          </div>
          <p className="text-center text-amber-700">
            Use them to get honest reviews for your books or start reviewing to earn more!
          </p>
        </div>

        {/* How Vibely Helps Authors */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            How Vibely Helps You as an Author
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-blue-600 rounded-full p-2">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Get Honest Reviews</h3>
              </div>
              <p className="text-gray-700">
                Connect with real readers who will read your book and leave genuine Amazon reviews. Build credibility and social proof for your work.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-purple-600 rounded-full p-2">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Boost Your Rankings</h3>
              </div>
              <p className="text-gray-700">
                More reviews mean better visibility on Amazon. Climb the rankings and reach more potential readers organically.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-green-600 rounded-full p-2">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Build Your Audience</h3>
              </div>
              <p className="text-gray-700">
                Reach readers in your target countries. Use our country-specific targeting to find the perfect audience for your book.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-orange-600 rounded-full p-2">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-800">Fair Exchange System</h3>
              </div>
              <p className="text-gray-700">
                Review other books to earn coins, then use those coins to get reviews for your own books. It's a win-win community!
              </p>
            </div>
          </div>
        </div>
        

        {/* Quick Start Guide */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-8 border border-indigo-200">
          <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">Quick Start Guide</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-indigo-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">1</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Upload Your Book</p>
                <p className="text-sm text-gray-600">Add your book details and set how many coins you'll pay per review</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="bg-indigo-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Get Reviews</p>
                <p className="text-sm text-gray-600">Readers will claim your book, read it, and post honest reviews on Amazon</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="bg-indigo-600 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <p className="font-semibold text-gray-800">Earn More Coins</p>
                <p className="text-sm text-gray-600">Review other authors' books to earn coins and keep the cycle going</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <button
            onClick={onContinue}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition shadow-lg flex items-center gap-2 mx-auto"
          >
            Continue to Terms & Conditions
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};



export default WelcomePage;